import { useRef, useState } from "react";

function timestamp(ms) {
  const seconds = Math.floor(ms / 1000);
  return String(Math.floor(seconds / 60)).padStart(2, "0") + ":" + String(seconds % 60).padStart(2, "0");
}

// Parent supplies an authorized audio URL and permission-checked API callbacks.
// No audio upload, transcription, inferred identity or report generation here.
export default function TranscriptEditor({ transcript, audioUrl, onCorrect, onApprove, onReload, onSplit, externalBusy = false }) {
  const player = useRef(null);
  const passageInput = useRef(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [showRaw, setShowRaw] = useState(false);
  const locked = busy || externalBusy;
  if (!transcript || !Array.isArray(transcript.edited_passages) || !Array.isArray(transcript.raw_passages)) {
    return <p role="status">Transcript unavailable.</p>;
  }
  const rawById = new Map(transcript.raw_passages.map((row) => [row.id, row]));
  const revisionChanged = Boolean(editing && editing.revision !== transcript.revision);

  function begin(row) {
    if (locked) return;
    if (editing && !window.confirm("Discard the unsaved changes to the selected passage?")) return;
    setEditing({ id: row.id, text: row.text, speaker_label: row.speaker_label, revision: transcript.revision });
    setError(""); setNotice("");
  }
  function listen(row) {
    if (!player.current) return;
    try {
      player.current.currentTime = row.start_ms / 1000;
      player.current.play().catch(() => setError("Press Play in the audio controls to listen."));
    } catch { setError("The recording is not ready. Try again after it has loaded."); }
  }
  async function save(event) {
    event.preventDefault();
    if (!editing || locked || revisionChanged) return;
    const selected = { ...editing };
    setBusy(true); setError(""); setNotice("");
    try {
      const result = await onCorrect(selected.id, {
        expected_revision: selected.revision, text: selected.text,
        speaker_label: selected.speaker_label,
        reason: "Editor corrected this selected passage.",
      });
      if (result?.revision !== selected.revision + 1) throw new Error("Confirmation is unavailable. Reload before making another correction.");
      setEditing(null);
      setNotice("The selected passage was corrected. Other passages were not renamed. Review and approve the updated transcript before creating a report.");
    } catch (failure) {
      setError(failure?.message || "The correction could not be confirmed. Your text is preserved.");
    } finally { setBusy(false); }
  }
  async function approve() {
    if (locked || editing || !window.confirm("Approve this complete transcript as the source for a report? Check words, speaker labels and unclear passages first.")) return;
    const revision = transcript.revision;
    setBusy(true); setError(""); setNotice("");
    try {
      const result = await onApprove(revision);
      if (result?.status !== "approved") throw new Error("Approval could not be confirmed. Reload to check the current version.");
      setNotice("Transcript approved. A report can now be based on this approved version.");
    } catch (failure) { setError(failure?.message || "Approval could not be confirmed."); }
    finally { setBusy(false); }
  }
  return <section className="space-y-5" aria-label="Review recording transcript">
    <header className="space-y-2">
      <h1 className="text-2xl font-semibold">Review transcript</h1>
      <p className="text-sm text-slate-600">Check the recording against the literal transcript. Correct each passage individually before approving it as a report source.</p>
      <p className="text-sm">Version {transcript.revision} · {transcript.status === "approved" ? "Approved" : "Draft — awaiting review"}</p>
    </header>
    {audioUrl ? <audio ref={player} src={audioUrl} controls preload="metadata"
      className="w-full" aria-label="Original recording" /> : <p>Original recording unavailable. Arrange access before verifying the transcript.</p>}
    {error && <p role="alert" className="rounded border border-amber-200 p-3">{error}</p>}
    {notice && <p role="status" className="rounded border p-3">{notice}</p>}
    <div className="flex flex-wrap gap-3">
      <button type="button" onClick={() => setShowRaw((value) => !value)} className="rounded border px-3 py-2">
        {showRaw ? "Hide original text" : "Compare original text"}
      </button>
      <button type="button" disabled={locked} onClick={() => onReload?.()} className="rounded border px-3 py-2">Refresh transcript</button>
    </div>
    <div className="max-h-[60vh] space-y-4 overflow-y-auto rounded-xl border p-4">
      {transcript.edited_passages.map((row) => {
        const raw = rawById.get(row.raw_passage_id || row.id);
        return <article key={row.id} className="space-y-3 border-b pb-4 last:border-b-0" aria-label={"Passage " + row.turn}>
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-medium">{row.speaker_label}</span>
            <span className="text-sm">{timestamp(row.start_ms)}–{timestamp(row.end_ms)} · Passage {row.turn}</span>
            {row.uncertain && <span className="text-sm text-amber-700">Recognition uncertain — check recording</span>}
            <button type="button" disabled={!audioUrl} onClick={() => listen(row)} className="rounded border px-2 py-1 text-sm">Listen from here</button>
            <button type="button" disabled={locked} onClick={() => begin(row)} className="rounded border px-2 py-1 text-sm">Correct this passage</button>
          </div>
          <p className="whitespace-pre-wrap break-words">{row.text}</p>
          {showRaw && raw && <div className="rounded bg-slate-50 p-3 text-sm">
            <p className="font-medium">Original: {raw.speaker_label}</p>
            <p className="whitespace-pre-wrap break-words">{raw.text}</p>
          </div>}
          {editing?.id === row.id && <form onSubmit={save} className="space-y-3 rounded border p-3">
            <p className="text-sm">Only this passage changes. Names and labels in other passages remain as they are.</p>
            {revisionChanged && <p role="alert">The transcript changed while you were editing. Your draft is preserved; compare it with the current passage before discarding and reopening this edit.</p>}
            <label className="block text-sm">Speaker label or name
              <input required maxLength={100} value={editing.speaker_label} disabled={locked}
                onChange={(event) => setEditing((previous) => ({ ...previous, speaker_label: event.target.value }))}
                className="mt-1 block w-full rounded border p-2" />
            </label>
            <label className="block text-sm">Literal passage text
              <textarea ref={passageInput} required maxLength={10000} rows={5} value={editing.text} disabled={locked}
                onChange={(event) => setEditing((previous) => ({ ...previous, text: event.target.value }))}
                className="mt-1 block w-full rounded border p-2" />
            </label>
            <div className="flex gap-3">
              <button disabled={locked || revisionChanged || !editing.text.trim() || !editing.speaker_label.trim()}
                className="rounded border px-3 py-2">Save this passage</button>
              {onSplit && <button type="button" disabled={locked || revisionChanged || editing.text !== row.text || editing.speaker_label !== row.speaker_label}
                className="rounded border px-3 py-2" onClick={async () => {
                  const cursor = passageInput.current?.selectionStart;
                  const offset = cursor == null ? 0 : Array.from(editing.text.slice(0, cursor)).length;
                  const splitTime = Math.round((player.current?.currentTime || 0) * 1000);
                  if (!offset || offset >= row.text.length || splitTime <= row.start_ms || splitTime >= row.end_ms) {
                    setError("Place the text cursor at the split and pause the recording at the matching time inside this passage."); return;
                  }
                  setBusy(true); setError("");
                  try {
                    const result = await onSplit(row.id, { expected_revision: editing.revision, offset, split_at_ms: splitTime });
                    if (result?.revision !== editing.revision + 1) throw new Error("Split confirmation unavailable. Refresh before retrying.");
                    setEditing(null); setNotice("Passage split. Assign names separately to each part; no other speaker labels changed.");
                  } catch (failure) { setError(failure.message || "Split could not be confirmed."); }
                  finally { setBusy(false); }
                }}>Split at text cursor and audio time</button>}
              <button type="button" disabled={locked} onClick={() => {
                if (window.confirm("Discard these unsaved passage changes?")) setEditing(null);
              }} className="rounded border px-3 py-2">Discard edit</button>
            </div>
          </form>}
        </article>;
      })}
    </div>
    <p className="text-sm text-slate-600">Speaker numbers identify successive speech turns. They are not automatically recognised identities. Review may require correcting words as well as names.</p>
    <button type="button" disabled={locked || Boolean(editing) || !audioUrl || transcript.status === "approved"}
      onClick={approve} className="zy-btn-primary">{busy ? "Please wait…" : "Approve complete transcript"}</button>
    <p className="text-sm">Approval does not send a document or create a report automatically.</p>
  </section>;
}
